import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { OrgBeneficiary } from '../../sanus/organizations/models/beneficiaries.model';
import { Pagination } from '../../shared/components/pagination/models/pagination.model';
import { PaginationArgs } from '../../shared/components/pagination/models/pagination-args.model';
import { MessageResponse } from '../../shared/models/message-response.model';
import { PrismaBodyQuery } from '../../shared/models/prisma-body-query.model';

import { BeneficiaryDto } from './dtos/beneficiary';
import { BeneficiaryDependentDto } from './dtos/beneficiary-dependent';
import { BeneficiaryDependentCreateDto } from './dtos/beneficiary-dependent-create';
import { BeneficiaryDependentDeleteDto } from './dtos/beneficiary-dependent-delete';
import { BeneficiaryDependentUpdateDto } from './dtos/beneficiary-dependent-update';
import { BeneficiaryUpdateDto } from './dtos/beneficiary-update';
import { BeneficiaryUpdateInactiveOrRetiredDto } from './dtos/beneficiary-update-inactive-or-retired';
import { BeneficiaryHistoryResponse } from './models/beneficiary-history-response.model';
import { BeneficiaryPendenciesResponse } from './models/beneficiary-pendencies-response.model';

@Injectable({
	providedIn: 'root',
})
export class BeneficiariesService {
	private readonly endpoint: string = `${environment.apiUrl}/beneficiary`;

	constructor(private readonly http: HttpClient) {}

	createDependent({
		dto,
		organizationId,
	}: {
		dto: BeneficiaryDependentCreateDto;
		organizationId: string;
	}): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(
			`${this.endpoint}/v1/create/dependent/${organizationId}`,
			dto,
		);
	}

	deleteBeneficiaryFile({
		fileId,
	}: {
		fileId: string;
	}): Observable<MessageResponse> {
		return this.http.delete<MessageResponse>(
			`${this.endpoint}/archives/${fileId}`,
		);
	}

	deleteDependency({
		beneficiaryId,
		query,
	}: {
		beneficiaryId: string;
		query: PrismaBodyQuery;
	}): Observable<MessageResponse> {
		const endpoint = `${this.endpoint}/${beneficiaryId}`;

		return this.http.patch<MessageResponse>(endpoint, query);
	}

	deleteDependent({
		dependentId,
		dto,
		organizationId,
	}: {
		dependentId: string;
		dto: BeneficiaryDependentDeleteDto;
		organizationId: string;
	}): Observable<MessageResponse> {
		return this.http.post<MessageResponse>(
			`${this.endpoint}/v1/${organizationId}/${dependentId}/retired-and-inactive`,
			dto,
		);
	}

	getBeneficiaries(
		args: PaginationArgs,
	): Observable<Pagination<OrgBeneficiary>> {
		return this.http.get<Pagination<OrgBeneficiary>>(this.endpoint, {
			params: args.buildHttpParams(),
		});
	}

	getHistory(beneficiaryId: string): Observable<BeneficiaryHistoryResponse[]> {
		const endpoint = `${this.endpoint}/${beneficiaryId}/history`;

		return this.http.get<BeneficiaryHistoryResponse[]>(endpoint);
	}

	getPendencies(
		beneficiaryId: string,
	): Observable<BeneficiaryPendenciesResponse> {
		const url = `${this.endpoint}/${beneficiaryId}/pendencies`;

		return this.http.get<BeneficiaryPendenciesResponse>(url);
	}

	patchPendencies({
		beneficiaryId,
		query,
	}: {
		beneficiaryId: string;
		query: PrismaBodyQuery;
	}): Observable<MessageResponse> {
		const url = `${this.endpoint}/${beneficiaryId}`;

		return this.http.patch<MessageResponse>(url, query);
	}

	searchBeneficiary({
		beneficiaryId,
		organizationId,
	}: {
		beneficiaryId: string;
		organizationId: string;
	}): Observable<BeneficiaryDto> {
		return this.http.get<BeneficiaryDto>(
			`${this.endpoint}/v1/${organizationId}/${beneficiaryId}`,
		);
	}

	searchDependent({
		dependentId,
		organizationId,
	}: {
		dependentId: string;
		organizationId: string;
	}): Observable<BeneficiaryDependentDto> {
		return this.http.get<BeneficiaryDependentDto>(
			`${this.endpoint}/v1/${organizationId}/${dependentId}`,
		);
	}

	updateActive({
		beneficiaryId,
		dto,
		organizationId,
	}: {
		beneficiaryId: string;
		dto: BeneficiaryUpdateDto;
		organizationId: string;
	}): Observable<MessageResponse> {
		return this.http.patch<MessageResponse>(
			`${this.endpoint}/${beneficiaryId}/organizations/${organizationId}`,
			dto,
		);
	}

	updateBeneficiaryFiles({
		beneficiaryId,
		dto,
		inactive,
		organizationId,
	}: {
		beneficiaryId: string;
		dto: FormData;
		inactive: boolean;
		organizationId: string;
	}): Observable<MessageResponse> {
		let url = `${this.endpoint}/archives/${organizationId}/${beneficiaryId}`;

		if (inactive) {
			url = `${this.endpoint}/v1/${beneficiaryId}/files/disconnected/${organizationId}`;
		}

		return this.http.post<MessageResponse>(url, dto);
	}

	updateDependent({
		dependentId,
		dto,
	}: {
		dependentId: string;
		dto: BeneficiaryDependentUpdateDto;
	}): Observable<MessageResponse> {
		return this.http.patch<MessageResponse>(
			`${this.endpoint}/dependents/${dependentId}`,
			dto,
		);
	}

	updateInactiveOrRetired({
		beneficiaryId,
		beneficiaryType,
		dto,
		organizationId,
	}: {
		beneficiaryId: string;
		beneficiaryType: string;
		dto: BeneficiaryUpdateInactiveOrRetiredDto;
		organizationId: string;
	}): Observable<MessageResponse> {
		const type = beneficiaryType === 'inativo' ? 'INACTIVE' : 'RETIRED';

		return this.http.patch<MessageResponse>(
			`${this.endpoint}/v1/${organizationId}/${beneficiaryId}/update-inactive-or-retired/${type}`,
			dto,
		);
	}

	updateOne({
		beneficiaryId,
		dto,
		organizationId,
	}: {
		beneficiaryId: string;
		dto: BeneficiaryUpdateDto;
		organizationId: string;
	}): Observable<MessageResponse> {
		return this.http.patch<MessageResponse>(
			`${this.endpoint}/v1/${organizationId}/${beneficiaryId}/update-beneficiary-after-created`,
			dto,
		);
	}
}
